// Pure site functions: no DOM, no requests - covered by tests.
import { t } from "./i18n";

export type Provider = "google" | "meta";

export const PROVIDER_NAMES: Record<Provider, string> = {
  google: "Google",
  meta: "Meta",
};

export function isProvider(v: unknown): v is Provider {
  return v === "google" || v === "meta";
}

/** Providers that can be connected as a data source (TikTok is not a sign-in method). */
export type ConnProvider = Provider | "tiktok" | "google_play" | "apple_ads";

export const CONN_PROVIDER_NAMES: Record<ConnProvider, string> = {
  ...PROVIDER_NAMES,
  tiktok: "TikTok",
  google_play: "Google Play",
  apple_ads: "Apple Ads",
};

export function isConnProvider(v: unknown): v is ConnProvider {
  return isProvider(v) || v === "tiktok" || v === "google_play" || v === "apple_ads";
}

const SOURCE_TITLES: Record<string, string> = {
  meta: "Meta Ads",
  fb_pages: "Facebook Pages",
  instagram: "Instagram",
  tiktok: "TikTok Ads",
  apple_ads: "Apple Ads",
  ga4: "GA4",
  google_ads: "Google Ads",
  search_console: "Search Console",
  google_play: "Google Play",
  app_store_connect: "App Store Connect",
};

export function sourceTitle(id: string): string {
  return SOURCE_TITLES[id] ?? id;
}

/** "Meta Ads, Facebook Pages, Instagram" for the card note; empty list gives "". */
export function sourcesList(ids: string[]): string {
  return ids.map(sourceTitle).join(", ");
}

/** The re-authorization notice of a Meta or Google card, or null when nothing is missing. */
export function reauthNotice(missing: string[] | undefined, provider: ConnProvider = "meta"): string | null {
  if (!missing || missing.length === 0) return null;
  const sources = missing.map(sourceTitle).join(" and ");
  return provider === "google" ? t("conn.reauthNoticeGoogle", { sources }) : t("conn.reauthNotice", { sources });
}

export interface ClientItem {
  id: "claude-desktop-extension" | "claude-skill" | "install-script";
  title: string;
  version: string;
  file: string;
  sha256: string;
  size_bytes: number;
}

export interface ClientsManifest {
  built_at: string;
  version: string;
  items: ClientItem[];
}

const CLIENT_IDS = ["claude-desktop-extension", "claude-skill", "install-script"];

/** Parses clients.json; anything malformed gives null (or the item is skipped). */
export function parseClients(raw: unknown): ClientsManifest | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.items)) return null;
  const items: ClientItem[] = [];
  for (const it of r.items) {
    if (typeof it !== "object" || it === null) continue;
    const i = it as Record<string, unknown>;
    if (
      typeof i.id !== "string" ||
      !CLIENT_IDS.includes(i.id) ||
      typeof i.file !== "string" ||
      i.file === "" ||
      i.file.includes("/") ||
      i.file.includes("..")
    ) {
      continue;
    }
    items.push({
      id: i.id as ClientItem["id"],
      title: typeof i.title === "string" ? i.title : i.id,
      version: typeof i.version === "string" ? i.version : "",
      file: i.file,
      sha256: typeof i.sha256 === "string" ? i.sha256 : "",
      size_bytes: typeof i.size_bytes === "number" ? i.size_bytes : 0,
    });
  }
  return {
    built_at: typeof r.built_at === "string" ? r.built_at : "",
    version: typeof r.version === "string" ? r.version : "",
    items,
  };
}

export function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "-";
  if (bytes < 1024) return `${bytes} ${t("size.b")}`;
  const units = [t("size.kb"), t("size.mb"), t("size.gb")];
  let v = bytes / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  const s = v >= 10 ? Math.round(v).toString() : v.toFixed(1);
  return `${s} ${units[u]}`;
}

/** Locale-neutral date: 2026-10-03T12:00:00Z -> 2026-10-03 (UTC, so the text does not depend on the machine). */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

const safeZone = (timeZone: string): string => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch {
    return "UTC";
  }
};

/** "Sep 27" out of an ISO date "2026-09-27" (the date as written, no zone shift). */
export function formatDay(isoDate: string): string {
  const d = new Date(`${isoDate.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric" }).format(d);
}

/** "Sep 27 – Oct 3" out of two ISO dates. */
export function formatRange(start: string, end: string): string {
  return start === end ? formatDay(start) : `${formatDay(start)} – ${formatDay(end)}`;
}

/** A moment in the user's zone, as in the mockups: "today, 09:12", "Oct 2", "Sep 30, 2025" for another year. */
export function formatWhen(iso: string | null | undefined, timeZone: string, now: Date = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const zone = safeZone(timeZone);
  const ymd = new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" });
  if (ymd.format(d) === ymd.format(now)) {
    const time = new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
    return t("account.today", { time });
  }
  const sameYear = ymd.format(d).slice(0, 4) === ymd.format(now).slice(0, 4);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(d);
}

/** "UTC+3", "UTC−7", "UTC+5:30" for a zone at the given moment; "" for UTC itself or when the browser cannot tell. */
export function utcOffset(timeZone: string, now: Date = new Date()): string {
  if (/^(Etc\/)?(UTC|GMT|Zulu|Universal)$/i.test(timeZone)) return "";
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "shortOffset" })
      .formatToParts(now)
      .find((p) => p.type === "timeZoneName")?.value;
    if (!part || !/^GMT/.test(part)) return "";
    const off = part.slice(3);
    return `UTC${off === "" ? "+0" : off.replace("-", "−")}`;
  } catch {
    return "";
  }
}

export interface ConnLike {
  configured: boolean;
  connected: boolean;
  account_label: string | null;
  expires_at: string | null;
}

export interface ConnectionView {
  text: string;
  /** The Authorize/Reauthorize button is available. */
  canAuthorize: boolean;
  authorizeLabel: string;
  canDisconnect: boolean;
}

export function connectionView(conn: ConnLike, needsReauthorization: string[] = []): ConnectionView {
  if (!conn.configured) {
    return {
      text: t("conn.notConfigured"),
      canAuthorize: false,
      authorizeLabel: t("conn.authorize"),
      canDisconnect: false,
    };
  }
  if (!conn.connected) {
    return {
      text: t("conn.notConnected"),
      canAuthorize: true,
      authorizeLabel: t("conn.authorize"),
      canDisconnect: false,
    };
  }
  const parts = [conn.account_label ?? ""].filter(Boolean);
  const until = formatDate(conn.expires_at);
  let text = t("conn.connected");
  if (parts.length) text += `: ${parts.join("")}`;
  if (until) text += `, ${t("conn.until", { date: until })}`;
  return {
    text,
    canAuthorize: true,
    authorizeLabel: needsReauthorization.length > 0 ? t("conn.reauthorizeNow") : t("conn.reauthorize"),
    canDisconnect: true,
  };
}

/** Sign-in link: full navigation, return_to is encoded. */
export function loginUrl(provider: Provider, returnTo?: string): string {
  const base = `/auth/${provider}`;
  return returnTo ? `${base}?return_to=${encodeURIComponent(returnTo)}` : base;
}

/** Same rule as the server's safeReturnTo: only a relative path with a single leading "/". */
export function safeReturnTo(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  if (!value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) return null;
  if (/[\r\n\\]/.test(value)) return null;
  return value;
}

export function consentPath(requestId: string): string {
  return `/oauth/consent?request=${encodeURIComponent(requestId)}`;
}

export interface QueryMessage {
  kind: "error" | "info";
  text: string;
}

/** Message from query parameters (`error`, `provider`, `connected`). */
export function queryMessage(search: string | URLSearchParams): QueryMessage | null {
  const q = typeof search === "string" ? new URLSearchParams(search) : search;
  const error = q.get("error");
  const providerRaw = q.get("provider");
  const name = isConnProvider(providerRaw) ? CONN_PROVIDER_NAMES[providerRaw] : null;
  const connected = q.get("connected");
  if (error === "provider_not_configured") {
    return {
      kind: "error",
      text: name
        ? t("msg.providerNotConfiguredLogin", { provider: name })
        : t("msg.appNotConfiguredLogin"),
    };
  }
  if (error === "login_failed") {
    return { kind: "error", text: t("msg.loginFailed") };
  }
  if (error === "email_not_verified") {
    return { kind: "error", text: t("msg.emailNotVerified") };
  }
  if (error === "not_signed_in") {
    return { kind: "error", text: t("msg.notSignedIn") };
  }
  if (error === "connect_failed") {
    return {
      kind: "error",
      text: name
        ? t("msg.connectFailedProvider", { provider: name })
        : t("msg.connectFailed"),
    };
  }
  if (isConnProvider(connected)) {
    return { kind: "info", text: t("msg.providerConnected", { provider: CONN_PROVIDER_NAMES[connected] }) };
  }
  return null;
}

/** For /connections: provider_not_configured is about connecting, not signing in. */
export function connectionsMessage(search: string | URLSearchParams): QueryMessage | null {
  const q = typeof search === "string" ? new URLSearchParams(search) : search;
  if (q.get("error") === "provider_not_configured") {
    const p = q.get("provider");
    return {
      kind: "error",
      text: isConnProvider(p)
        ? t("msg.providerAppNotConfigured", { provider: CONN_PROVIDER_NAMES[p] })
        : t("msg.appNotConfiguredLogin"),
    };
  }
  if (q.get("error") === "unknown_client") return { kind: "error", text: t("clients.errUnknown") };
  // Apple Ads (ADR-0065): the Apple Account granted no ad account V-Kit can read - the connection is not saved.
  if (q.get("error") === "apple_ads_no_accounts") return { kind: "error", text: t("msg.appleAdsNoAccounts") };
  return queryMessage(q);
}

export function installCommand(origin: string): string {
  return `curl -fsSL ${origin}/clients/install.sh | bash`;
}

/** Clients per account when the server has not said otherwise (ADR-0062: MK_CLIENTS_MAX, default 3).
 *  The one place the site takes «three» from (BL-194: banner, /for-agencies, /pricing, /subscribe, /vs, price.faqA);
 *  keep it equal to the server default in server/src/config.ts. */
export const CLIENTS_DEFAULT_MAX = 3;

const COUNT_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
/** 3 → "three" for running text; larger numbers stay digits. */
export const countWord = (n: number): string => COUNT_WORDS[n] ?? String(n);

/** «three» - CLIENTS_DEFAULT_MAX as a word for the marketing texts (BL-194). */
export const CLIENTS_WORD = countWord(CLIENTS_DEFAULT_MAX);

/** «New» next to the clients line on /pricing and /subscribe (BL-194): New until 2026-11-15 - then set to false. */
export const CLIENTS_NEW = true;
